import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { ConfirmModal } from '@/components/play/ConfirmModal';
import { listAccounts, createAccount, updateAccount, deleteAccount, type PlayAccount } from '@/api/play';

type ModalMode = 'create' | 'edit' | 'delete' | null;

const fmtUnix = (s: number) => new Date(s * 1000).toLocaleDateString();

export function Play() {
  const qc = useQueryClient();
  const [mode, setMode] = useState<ModalMode>(null);
  const [selected, setSelected] = useState<PlayAccount | null>(null);
  const [form, setForm] = useState({ name: '', sa_json: '', enabled: true, gcs_bucket_id: '', developer_name: '' });
  const [formError, setFormError] = useState<string | null>(null);

  const { data: accounts = [], isLoading, error, refetch } = useQuery({
    queryKey: ['play-accounts'],
    queryFn: listAccounts,
  });

  const close = () => { setMode(null); setSelected(null); setFormError(null); };
  const invalidate = () => qc.invalidateQueries({ queryKey: ['play-accounts'] });

  const createMut = useMutation({
    mutationFn: () => createAccount({
      name: form.name.trim(),
      ...(form.sa_json.trim() ? { sa_json: form.sa_json.trim() } : {}),
    }),
    onSuccess: () => { invalidate(); close(); },
    onError: (e: any) => setFormError(e?.message ?? 'Failed to add account'),
  });

  const updateMut = useMutation({
    mutationFn: () => updateAccount(selected!.id, {
      name: form.name.trim(),
      enabled: form.enabled,
      gcs_bucket_id: form.gcs_bucket_id.trim(),
      developer_name: form.developer_name.trim(),
      ...(form.sa_json.trim() ? { sa_json: form.sa_json.trim() } : {}),
    }),
    onSuccess: () => { invalidate(); close(); },
    onError: (e: any) => setFormError(e?.message ?? 'Failed to update account'),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteAccount(selected!.id),
    onSuccess: () => { invalidate(); close(); },
    onError: (e: any) => setFormError(e?.message ?? 'Failed to delete account'),
  });

  const openCreate = () => { setForm({ name: '', sa_json: '', enabled: true, gcs_bucket_id: '', developer_name: '' }); setFormError(null); setMode('create'); };
  const openEdit = (a: PlayAccount) => {
    setSelected(a);
    setForm({ name: a.name, sa_json: '', enabled: !!a.enabled, gcs_bucket_id: a.gcs_bucket_id ?? '', developer_name: a.developer_name ?? '' });
    setFormError(null);
    setMode('edit');
  };
  const openDelete = (a: PlayAccount) => { setSelected(a); setFormError(null); setMode('delete'); };

  const saving = createMut.isPending || updateMut.isPending;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-content">Play Console Accounts</h1>
        <Button onClick={openCreate}>Add account</Button>
      </div>

      {error ? (
        <div className="bg-surface border border-border rounded-lg p-6 text-center">
          <p className="text-sm text-red-400 mb-3">{(error as Error).message}</p>
          <Button variant="secondary" size="sm" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : (
        <DataTable<PlayAccount>
          loading={isLoading}
          data={accounts}
          keyExtractor={(a) => String(a.id)}
          columns={[
            {
              key: 'name', label: 'Name',
              render: (a) => <Link to={`/play/${a.id}`} className="text-accent hover:underline">{a.name}</Link>,
            },
            {
              key: 'sa_client_email', label: 'Service account',
              render: (a) => a.sa_client_email
                ? <span className="text-content-muted">{a.sa_client_email}</span>
                : <span className="text-amber-400 text-xs">no API key yet</span>,
            },
            {
              key: 'enabled', label: 'Status',
              render: (a) => (
                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                  a.enabled ? 'bg-green-500/15 text-green-400' : 'bg-surface-muted text-content-muted'
                }`}>
                  {a.enabled ? 'Enabled' : 'Disabled'}
                </span>
              ),
            },
            {
              key: 'last_error', label: 'Last error',
              render: (a) => a.last_error
                ? <span className="text-red-400 max-w-[16rem] truncate inline-block align-bottom" title={a.last_error}>{a.last_error}</span>
                : <span className="text-content-muted">—</span>,
            },
            {
              key: 'created_at', label: 'Created',
              render: (a) => <span className="text-content-muted">{fmtUnix(a.created_at)}</span>,
            },
            {
              key: 'actions', label: '',
              render: (a) => (
                <span className="flex justify-end gap-2">
                  <Button size="sm" variant="secondary" onClick={() => openEdit(a)}>Edit</Button>
                  <Button size="sm" variant="secondary" onClick={() => openDelete(a)}>Delete</Button>
                </span>
              ),
            },
          ]}
        />
      )}

      <Modal
        isOpen={mode === 'create' || mode === 'edit'}
        onClose={close}
        title={mode === 'edit' ? 'Edit Play account' : 'Add Play account'}
        size="lg"
      >
        <label className="block text-sm text-content-muted mb-1">Name</label>
        <input
          className="w-full mb-3 px-3 py-2 rounded-md bg-background border border-border text-content text-sm"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="e.g. Acct 01"
        />
        <label className="block text-sm text-content-muted mb-1">Service account JSON</label>
        <textarea
          className="w-full px-3 py-2 rounded-md bg-background border border-border text-content font-mono text-xs h-40"
          value={form.sa_json}
          onChange={(e) => setForm({ ...form, sa_json: e.target.value })}
          placeholder='{ "client_email": "...", "private_key": "..." }'
        />
        <p className="text-xs text-content-muted mt-1 mb-3">
          {mode === 'edit' ? 'Leave blank to keep the current key. ' : 'Optional at this point — the account can start as a console login and get its API key once the developer account is registered. '}
          Credentials are verified against the Android Publisher API before saving.
        </p>
        {mode === 'edit' && (
          <label className="flex items-center gap-2 text-sm text-content mb-3">
            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            Enabled
          </label>
        )}
        {mode === 'edit' && (
          <>
            <label className="block text-sm text-content-muted mb-1">Cloud Storage bucket id</label>
            <input
              className="w-full mb-1 px-3 py-2 rounded-md bg-background border border-border text-content text-sm"
              value={form.gcs_bucket_id}
              onChange={(e) => setForm({ ...form, gcs_bucket_id: e.target.value })}
              placeholder="pubsite_prod_..."
            />
            <p className="text-xs text-content-muted mt-1 mb-3">
              From Play Console → Download reports → Cloud Storage URI; needed for keyword acquisition.
            </p>
            <label className="block text-sm text-content-muted mb-1">Developer name</label>
            <input
              className="w-full mb-1 px-3 py-2 rounded-md bg-background border border-border text-content text-sm"
              value={form.developer_name}
              onChange={(e) => setForm({ ...form, developer_name: e.target.value })}
              maxLength={64}
              placeholder="As the store page shows it"
            />
            <p className="text-xs text-content-muted mt-1 mb-3">
              Builds of this console's apps send it as the company name.
            </p>
          </>
        )}
        {formError && <p className="text-red-400 text-sm mb-3">{formError}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close} disabled={saving}>Cancel</Button>
          <Button
            loading={saving}
            disabled={!form.name.trim()}
            onClick={() => (mode === 'edit' ? updateMut.mutate() : createMut.mutate())}
          >
            {mode === 'edit' ? 'Save' : 'Add'}
          </Button>
        </div>
      </Modal>

      <ConfirmModal
        isOpen={mode === 'delete'}
        title="Delete Play account"
        danger
        confirmLabel="Delete"
        loading={deleteMut.isPending}
        error={formError}
        message={
          <>
            Delete <strong className="text-content">{selected?.name}</strong>? Its registered apps are
            removed from this dashboard. Nothing changes in Google Play itself.
          </>
        }
        onConfirm={() => deleteMut.mutate()}
        onClose={close}
      />
    </div>
  );
}
